/* Scroll reveal — the resilient half of Dawn's animations.js.
 *
 * .animate--slide-in sits at opacity 0.01 until something removes
 * .scroll-trigger--offscreen, so every failure mode of the reveal is the same
 * failure: content that never appears. animations.js observes once, at
 * DOMContentLoaded, and unobserves on reveal - anything inserted afterwards
 * (a Swiper clone, a section the editor re-rendered, a bfcache restore on
 * iOS Safari) has nothing left to reveal it. This keeps watching, and treats
 * "visible but unanimated" as the correct answer whenever anything is wrong.
 *
 * It also owns the cascade. Dawn writes --animation-order with
 * setAttribute('style', ...), which REPLACES the whole inline style - and the
 * op- atoms keep their typography in inline --op-t-* properties, so letting
 * Dawn touch them would wipe every per-atom size, weight and padding. Hence
 * data-op-cascade rather than data-cascade: Dawn ignores ours, and the order is
 * set here with setProperty, which is additive.
 */
(function () {
  'use strict';

  var TRIGGER = 'scroll-trigger';
  var OFFSCREEN = 'scroll-trigger--offscreen';
  var READY = 'op-reveal-ready';
  var root = document.documentElement;

  // Safari 12.0 and iOS 12.1 and earlier have no IntersectionObserver. Drop the
  // gate class and everything renders as static, fully visible content.
  if (!('IntersectionObserver' in window)) {
    root.classList.remove(READY);
    return;
  }

  // Tells the head watchdog the machinery is alive, so it stops counting down.
  window.__opRevealOk = true;

  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var el = entries[i].target;
        if (!entries[i].isIntersecting) {
          el.classList.add(OFFSCREEN);
          continue;
        }
        el.classList.remove(OFFSCREEN);
        observer.unobserve(el);
      }
    },
    // Same trigger line as animations.js: 50px before the element reaches the
    // bottom of the viewport.
    { rootMargin: '0px 0px -50px 0px' }
  );

  // The cascade counts across a whole group, not among an element's immediate
  // siblings: the block system gives every atom its own wrapper, so a sibling
  // index is always 0 and nothing staggers. A group is the section or content
  // group the atoms were authored into, which is the unit a reader takes in at
  // once and so the unit the stagger should describe.
  var GROUP = '.op-cg, .op-sec-head, .op-container, .footer, section';

  function indexGroup(group) {
    if (group.getAttribute('data-op-cascaded') === '1') return;
    group.setAttribute('data-op-cascaded', '1');
    var members = group.querySelectorAll('[data-op-cascade]');
    for (var i = 0; i < members.length; i++) {
      // Past half a second of stagger the last item reads as broken rather
      // than choreographed, so the ramp stops there.
      // setProperty leaves the rest of the inline style alone - unlike Dawn's
      // setAttribute - and takes a string, because Safari ignores a number.
      members[i].style.setProperty('--animation-order', String(Math.min(i, 7)));
    }
  }

  // A transform makes an element the containing block for its position: fixed
  // descendants, and the slide-in leaves a transform behind - translateY(0) is
  // still a transform. A wrapper holding the sticky add-to-cart bar or the cart
  // drawer would pin it to itself instead of the viewport, so those never
  // animate. The CSS says the same thing with :has(); this is the belt for the
  // browsers that lack it, and it costs one selector per trigger.
  var NEVER_REVEAL = '.op-satc, .drawer, cart-drawer, [data-op-no-reveal]';

  function arm(el) {
    if (!el || el.getAttribute('data-op-armed') === '1') return;
    el.setAttribute('data-op-armed', '1');

    if (el.querySelector && el.querySelector(NEVER_REVEAL)) {
      el.classList.remove('animate--slide-in', 'animate--fade-in');
      el.classList.remove(OFFSCREEN);
      return;
    }

    if (el.hasAttribute('data-op-cascade')) {
      var group = el.closest ? el.closest(GROUP) : null;
      if (group) indexGroup(group);
    }

    observer.observe(el);
  }

  function armAll(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.classList && node.classList.contains(TRIGGER)) arm(node);
    var found = node.querySelectorAll ? node.querySelectorAll('.' + TRIGGER) : [];
    for (var i = 0; i < found.length; i++) arm(found[i]);
  }

  // Force-show anything still hidden, animation or not. Used by every backstop.
  function showAll(node) {
    var scope = node && node.querySelectorAll ? node : document;
    var stuck = scope.querySelectorAll('.' + TRIGGER + '.' + OFFSCREEN);
    for (var i = 0; i < stuck.length; i++) stuck[i].classList.remove(OFFSCREEN);
  }

  armAll(document.body || document);

  // Nodes that arrive later. Only added subtrees are inspected, and arming is
  // idempotent, so this stays cheap on a long page.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) armAll(added[j]);
      }
    }).observe(root, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (e) {
    armAll(e.target || document.body);
  });

  // iOS Safari restores from bfcache with the DOM exactly as it was left, and
  // fires no load event. Anything mid-reveal at the time would stay mid-reveal.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      armAll(document.body);
      showAll(document);
    }
  });

  // A hidden or occluded tab freezes the document timeline, so an element can
  // have its reveal triggered and the animation never advance. On the way back
  // to visible, anything still holding the offscreen class is simply shown.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      setTimeout(function () {
        var stuck = document.querySelectorAll('.' + TRIGGER + '.' + OFFSCREEN);
        for (var i = 0; i < stuck.length; i++) {
          var r = stuck[i].getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) stuck[i].classList.remove(OFFSCREEN);
        }
      }, 100);
    }
  });

  // Last resort: once the page has settled, show anything inside the viewport
  // that is somehow still waiting.
  window.addEventListener('load', function () {
    setTimeout(function () {
      var stuck = document.querySelectorAll('.' + TRIGGER + '.' + OFFSCREEN);
      for (var i = 0; i < stuck.length; i++) {
        var r = stuck[i].getBoundingClientRect();
        if (r.top < window.innerHeight - 50 && r.bottom > 0) stuck[i].classList.remove(OFFSCREEN);
      }
    }, 500);
  });
})();
